/**
 * @fileoverview Feature-scoped service for Cost of Sold Goods API.
 * @module features/cogs/api/costOfSoldGoodsService
 */

import apiClient from '@/lib/apiClient'
import {
  CostOfSoldGoodsParamsSchema,
  CostOfSoldGoodsResponseSchema,
  type CostOfSoldGoodsParams,
  type CostOfSoldGoodsResponseDTO,
} from '@/features/cogs/schemas/costOfSoldGoodsSchema'

/**
 * Service for fetching and exporting Cost of Sold Goods.
 */
export const costOfSoldGoodsService = {
  /**
   * Fetches the cost of sold goods for a given month.
   *
   * @param params Query params containing month in MM-yyyy format (UTC-normalized)
   * @returns Parsed and validated response DTO
   * @throws Error when validation fails or request errors
   * @example
   * ```ts
   * const res = await costOfSoldGoodsService.getCostOfSoldGoods({ month: "05-2025" });
   * ```
   */
  async getCostOfSoldGoods(params: CostOfSoldGoodsParams): Promise<CostOfSoldGoodsResponseDTO> {
    const safeParams = CostOfSoldGoodsParamsSchema.parse(params)
    const response = await apiClient.get('/cost-of-sold-goods', { params: safeParams })
    return CostOfSoldGoodsResponseSchema.parse(response.data)
  },

  /**
   * Exports the cost of sold goods as a CSV Blob for a given month.
   *
   * @param params Query params containing month in MM-yyyy format (UTC-normalized)
   * @returns CSV Blob
   * @throws Error when request fails
   * @example
   * ```ts
   * const blob = await costOfSoldGoodsService.exportCostOfSoldGoods({ month: "05-2025" });
   * ```
   */
  async exportCostOfSoldGoods(params: CostOfSoldGoodsParams): Promise<Blob> {
    const safeParams = CostOfSoldGoodsParamsSchema.parse(params)
    const response = await apiClient.get('/cost-of-sold-goods/export', {
      params: safeParams,
      responseType: 'blob',
    })
    return response.data as Blob
  },
}
